import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiPlus, FiEdit, FiTrash2, FiSave, FiSettings, FiClock, FiUsers, FiCalendar, FiInfo, FiShield, FiSearch
} from 'react-icons/fi';
import { settingsAPI, usersAPI } from '../../services/api';
import { DocumentTemplateSettings } from '../../components/Common/DocumentGenerator';
import JobDocumentsSettings from '../../components/Common/JobDocumentsSettings';
import Modal from '../../components/Common/Modal';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';

const BASE_TABS = [
  { key: 'courses', label: 'Courses', fields: ['course_name', 'program_type', 'duration', 'rncp_no', 'rncp_title', 'rncp', 'status'] },
  { key: 'program_types', label: 'Program Types', fields: ['program_type_name', 'display_order', 'status'] },
  { key: 'intakes', label: 'Intakes', fields: ['intake_name', 'year', 'status'] },
  { key: 'sources', label: 'Lead Sources', fields: ['source_name', 'status'] },
  { key: 'meeting_types', label: 'Meeting Types', fields: ['type_name', 'status'] },
  { key: 'prospect_statuses', label: 'Prospect Statuses', fields: ['prospect_group', 'status_name', 'display_order', 'status'] },
  { key: 'lead_statuses', label: 'Lead Statuses', fields: ['lead_group', 'status_name', 'status'] },
  { key: 'student_statuses', label: 'Student Statuses', fields: ['status_name', 'display_order', 'auto_trigger', 'locks_pipeline', 'status'] },
  { key: 'job_student_statuses', label: 'Job Student Statuses', fields: ['status_name', 'display_order', 'status'], franceOnly: true },
  { key: 'student_company_statuses', label: 'Company Statuses', fields: ['status_name', 'display_order', 'status'], franceOnly: true },
  { key: 'prospect_round_robin', label: 'Prospect Round Robin', fields: [], franceOnly: true },
  { key: 'job_documents', label: 'Job Documents', fields: [], franceOnly: true },
  { key: 'visa_statuses', label: 'Visa Statuses', fields: ['status_name', 'status'] },
  { key: 'visa_results', label: 'Visa Results', fields: ['result_name', 'status'] },
  { key: 'campuses', label: 'Campuses', fields: ['campus_name', 'mock_interview_enabled', 'status'] },
  { key: 'course_campus_fees', label: 'Course Campus Fees', fields: ['course_id', 'campus_id', 'default_fee', 'currency', 'status'] },
  { key: 'document_types', label: 'Documents', fields: ['document_type_name', 'is_required', 'is_passport', 'status'] },
  { key: 'visa_document_types', label: 'Visa Documents', fields: ['document_type_name', 'is_required', 'is_passport', 'status'] },
  { key: 'interview_timezones', label: 'Interview Timezones', fields: ['timezone_name', 'timezone_label', 'status'] },
  { key: 'interview_slots', label: 'Interview Slots', fields: ['campus_id', 'day_of_week', 'start_time', 'end_time', 'interview_capacity', 'mock_capacity', 'status'] },
  { key: 'mailboxes', label: 'Mail Configuration', fields: ['mailbox_name', 'mailbox_email', 'azure_tenant_id', 'azure_client_id', 'azure_client_secret', 'webhook_client_state', 'is_default', 'is_active', 'send_restriction'] },
  { key: 'zoom_hosts', label: 'Zoom Hosts', fields: [] },
  { key: 'document_templates', label: 'Document Templates', fields: [] },
  { key: 'internship_programs', label: 'Internship Programs', fields: ['program_name', 'display_order', 'status'], franceOnly: true },
  { key: 'status_desired_options', label: 'Status Desired', fields: ['option_name', 'display_order', 'status'], franceOnly: true },
  { key: 'business_specializations', label: 'Business Specializations', fields: ['specialization_name', 'display_order', 'status'], franceOnly: true },
  { key: 'how_found_options', label: 'How Found School Options', fields: ['option_name', 'option_name_fr', 'display_order', 'status'], franceOnly: true },
];

/** Must match backend utils/studentPipelineStatus.js TRIGGER_STAGE keys */
const STUDENT_AUTO_TRIGGER_OPTIONS = [
  { value: '', labelKey: 'settings.studentAutoTriggers.manual' },
  { value: 'student_created', labelKey: 'settings.studentAutoTriggers.student_created' },
  { value: 'application_submitted', labelKey: 'settings.studentAutoTriggers.application_submitted' },
  { value: 'application_fee_pending', labelKey: 'settings.studentAutoTriggers.application_fee_pending' },
  { value: 'application_fee_paid', labelKey: 'settings.studentAutoTriggers.application_fee_paid' },
  { value: 'mock_interview_fail', labelKey: 'settings.studentAutoTriggers.mock_interview_fail' },
  { value: 'mock_interview_pass', labelKey: 'settings.studentAutoTriggers.mock_interview_pass' },
  { value: 'interview_fail', labelKey: 'settings.studentAutoTriggers.interview_fail' },
  { value: 'interview_pass', labelKey: 'settings.studentAutoTriggers.interview_pass' },
  { value: 'application_accepted', labelKey: 'settings.studentAutoTriggers.application_accepted' },
  { value: 'tuition_fee_pending', labelKey: 'settings.studentAutoTriggers.tuition_fee_pending' },
  { value: 'tuition_fee_paid', labelKey: 'settings.studentAutoTriggers.tuition_fee_paid' },
  { value: 'letter_generated', labelKey: 'settings.studentAutoTriggers.letter_generated' },
  { value: 'letter_signed', labelKey: 'settings.studentAutoTriggers.letter_signed' },
  { value: 'visa_in_progress', labelKey: 'settings.studentAutoTriggers.visa_in_progress' },
  { value: 'visa_approved', labelKey: 'settings.studentAutoTriggers.visa_approved' },
  { value: 'visa_rejected', labelKey: 'settings.studentAutoTriggers.visa_rejected' }
];

const TAB_I18N = {
  courses: 'settings.tabs.courses',
  program_types: 'settings.tabs.programTypes',
  intakes: 'settings.tabs.intakes',
  sources: 'settings.tabs.leadSources',
  meeting_types: 'settings.tabs.meetingTypes',
  prospect_statuses: 'settings.tabs.prospectStatuses',
  lead_statuses: 'settings.tabs.leadStatuses',
  student_statuses: 'settings.tabs.studentStatuses',
  job_student_statuses: 'settings.tabs.jobStudentStatuses',
  student_company_statuses: 'settings.tabs.companyStatuses',
  prospect_round_robin: 'settings.tabs.prospectRoundRobin',
  job_documents: 'settings.tabs.jobDocuments',
  visa_statuses: 'settings.tabs.visaStatuses',
  visa_results: 'settings.tabs.visaResults',
  campuses: 'settings.tabs.campuses',
  course_campus_fees: 'settings.tabs.coursesCampusFees',
  document_types: 'settings.tabs.documents',
  visa_document_types: 'settings.tabs.visaDocuments',
  interview_timezones: 'settings.tabs.interviewTimezones',
  interview_slots: 'settings.tabs.interviewSlots',
  mailboxes: 'settings.tabs.mailConfig',
  zoom_hosts: 'settings.tabs.zoomHosts',
  document_templates: 'settings.tabs.documentTemplates',
  mail_send_window: 'settings.tabs.mailSendHours',
  internship_programs: 'settings.tabs.internshipPrograms',
  status_desired_options: 'settings.tabs.statusDesired',
  business_specializations: 'settings.tabs.businessSpecializations',
  how_found_options: 'settings.tabs.howFoundOptions'
};

/** Styles for Mail send hours panel */
const MS = {
  shell: { maxWidth: 920, margin: '0 auto' },
  card: {
    borderRadius: 16,
    border: '1px solid var(--border)',
    background: 'var(--white)',
    boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden'
  },
  hero: {
    padding: '24px 26px',
    background: 'linear-gradient(135deg, #f8fafc 0%, #fff 50%, #f1f5f9 100%)',
    borderBottom: '1px solid var(--border-light, #e2e8f0)'
  },
  heroTitle: { margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)' },
  heroSub: { margin: '8px 0 0', fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: 640 },
  section: { padding: '22px 26px', borderBottom: '1px solid var(--border-light, #e8e8e8)' },
  sectionLast: { padding: '22px 26px' },
  rowHead: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
    color: '#4f46e5',
    flexShrink: 0
  },
  secTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' },
  secHint: { margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.45 },
  callout: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '12px 14px',
    borderRadius: 10,
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    color: '#0c4a6e',
    fontSize: 13,
    lineHeight: 1.5
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '16px 18px',
    borderRadius: 12,
    border: '1px solid var(--border)',
    background: 'var(--white)',
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
  },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 },
  label: { display: 'block', marginBottom: 8, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, background: 'var(--white)' },
  timeInput: { padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 15, minWidth: 120 },
  chip: (on) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    padding: '12px 14px',
    borderRadius: 12,
    border: `2px solid ${on ? '#4f46e5' : 'var(--border)'}`,
    background: on ? '#f5f3ff' : 'var(--white)',
    cursor: 'pointer',
    transition: 'all 0.15s',
    minHeight: 72
  }),
  chipTitle: { fontSize: 14, fontWeight: 600, color: 'var(--text)' },
  chipHint: { fontSize: 12, color: 'var(--text-muted)' },
  dayRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  dayChip: (on) => ({
    minWidth: 44,
    padding: '10px 12px',
    borderRadius: 10,
    border: `1px solid ${on ? '#4f46e5' : 'var(--border)'}`,
    background: on ? '#eef2ff' : 'var(--white)',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    color: on ? '#3730a3' : 'var(--text-muted)',
    textAlign: 'center'
  }),
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    padding: '18px 26px',
    background: '#fafafa',
    borderTop: '1px solid var(--border-light, #e8e8e8)'
  }
};

const Settings = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { activeCountry } = useCountry();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo(() => {
    let t = [...BASE_TABS];
    if (!isFranceCRM) {
      t = t.filter((x) => !x.franceOnly);
    }
    if (isFranceCRM) {
      t = t.map((tab) => {
        if (tab.key !== 'courses') return tab;
        if ((tab.fields || []).includes('course_name_fr')) return tab;
        return { ...tab, fields: ['course_name', 'course_name_fr', ...(tab.fields || []).filter((f) => f !== 'course_name')] };
      });
    }
    const role = String(user?.role || '').toLowerCase();
    if (!['super_admin', 'admin'].includes(role)) {
      const idx = t.findIndex((x) => x.key === 'interview_slots');
      if (idx >= 0) t.splice(idx, 1);
    }
    if (role === 'super_admin') {
      t.push({ key: 'mail_send_window', label: 'Mail send hours', fields: [] });
    }
    return t;
  }, [user?.role, isFranceCRM]);

  const tabKeys = useMemo(() => tabs.map((x) => x.key), [tabs]);

  const activeTab = useMemo(() => {
    const param = searchParams.get('tab');
    if (param && tabKeys.includes(param)) return param;
    return tabKeys[0] || 'courses';
  }, [searchParams, tabKeys]);

  useEffect(() => {
    const param = searchParams.get('tab');
    if (param && !tabKeys.includes(param)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tabKeys[0] || 'courses');
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, tabKeys, setSearchParams]);

  const setActiveTab = useCallback(
    (key) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', key);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({});
  const [courses, setCourses] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [permittedUserIds, setPermittedUserIds] = useState([]);
  const [mailboxUserSearch, setMailboxUserSearch] = useState('');
  const [systemMailboxId, setSystemMailboxId] = useState('');
  const [systemMailboxLoading, setSystemMailboxLoading] = useState(false);
  const [systemMailboxSaving, setSystemMailboxSaving] = useState(false);
  const [mailWindowForm, setMailWindowForm] = useState({
    french_hours_enabled: false,
    timezone: 'Europe/Paris',
    window_start: '08:00',
    window_end: '17:00',
    weekdays: [1, 2, 3, 4, 5],
    restricted_roles: ['sales_executive', 'interviewer']
  });
  const [mailWindowLoading, setMailWindowLoading] = useState(false);
  const [slotBulkForm, setSlotBulkForm] = useState({
    campus_id: '',
    weekdays: [1, 2, 3, 4, 5],
    window_start: '10:00',
    window_end: '17:00',
    slot_minutes: 60,
    interview_capacity: 2,
    mock_capacity: 2
  });
  const [slotBulkSaving, setSlotBulkSaving] = useState(false);

  const activeUserCandidates = useMemo(
    () => (allUsers || []).filter((u) => String(u?.status || '').toLowerCase() !== 'inactive'),
    [allUsers]
  );

  const filteredMailboxUsers = useMemo(() => {
    const q = String(mailboxUserSearch || '').trim().toLowerCase();
    if (!q) return activeUserCandidates;
    return activeUserCandidates.filter((u) => {
      const name = String(u?.name || '').toLowerCase();
      const email = String(u?.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [activeUserCandidates, mailboxUserSearch]);

  const selectedMailboxUsers = useMemo(() => {
    const byId = new Map((allUsers || []).map((u) => [String(u.user_id), u]));
    return permittedUserIds.map((id) => byId.get(String(id))).filter(Boolean);
  }, [allUsers, permittedUserIds]);

  const [zoomHostsLoading, setZoomHostsLoading] = useState(false);
  const [zoomHostsSaving, setZoomHostsSaving] = useState(false);
  const [zoomHostsForm, setZoomHostsForm] = useState({
    interview_host_email: '',
    mock_interview_host_email: '',
    meeting_host_email: ''
  });
  const [rrLoading, setRrLoading] = useState(false);
  const [rrSaving, setRrSaving] = useState(false);
  const [rrUserSearch, setRrUserSearch] = useState('');
  const [roundRobinForm, setRoundRobinForm] = useState({
    enabled: false,
    balance_by_status: true,
    preferred_status: '',
    user_ids: []
  });
  const [websiteLeadStatuses, setWebsiteLeadStatuses] = useState([]);

  const rrUsers = useMemo(() => {
    const q = String(rrUserSearch || '').trim().toLowerCase();
    const eligible = (activeUserCandidates || []).filter((u) => {
      const roleOk = ['sales_executive', 'manager', 'sales_manager', 'admin', 'super_admin']
        .includes(String(u?.role || '').toLowerCase());
      const accessCountry = String(u?.access_country || '').toUpperCase();
      const cc = String(u?.country_code || u?.country || '').toUpperCase();
      const countryOk = accessCountry === 'FRANCE' || accessCountry === 'BOTH' || cc === 'FRANCE';
      return roleOk && countryOk;
    });
    if (!q) return eligible;
    return eligible.filter((u) => {
      const name = String(u?.name || '').toLowerCase();
      const email = String(u?.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [activeUserCandidates, rrUserSearch]);

  const [selectedSlotIds, setSelectedSlotIds] = useState([]);
  /** Interview Slots: which weekday tab is active (1=Mon … 7=Sun) */
  const [slotWeekdayTab, setSlotWeekdayTab] = useState(1);

  const MAIL_TIMEZONES = useMemo(
    () => [
      'Europe/Paris',
      'Europe/London',
      'Europe/Berlin',
      'Asia/Kolkata',
      'Asia/Dubai',
      'Asia/Singapore',
      'Asia/Tokyo',
      'America/New_York',
      'America/Chicago',
      'America/Los_Angeles',
      'UTC'
    ],
    []
  );

  const MAIL_WEEKDAYS = useMemo(
    () => [
      { n: 1, label: 'Mon' },
      { n: 2, label: 'Tue' },
      { n: 3, label: 'Wed' },
      { n: 4, label: 'Thu' },
      { n: 5, label: 'Fri' },
      { n: 6, label: 'Sat' },
      { n: 7, label: 'Sun' }
    ],
    []
  );

  const SLOT_WEEKDAYS = MAIL_WEEKDAYS;

  const toggleBulkWeekday = (n) => {
    setSlotBulkForm((f) => {
      const set = new Set(f.weekdays || []);
      if (set.has(n)) set.delete(n);
      else set.add(n);
      return { ...f, weekdays: [...set].sort((a, b) => a - b) };
    });
  };

  const toMin = (hhmm) => {
    const s = String(hhmm || '');
    if (!/^\d{2}:\d{2}$/.test(s)) return NaN;
    const [h, m] = s.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
    return h * 60 + m;
  };

  const fromMin = (mins) => {
    const m = Math.max(0, Math.min(24 * 60 - 1, mins));
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const buildSlotsFromWindow = ({ start, end, minutes }) => {
    const s = toMin(start);
    const e = toMin(end);
    const step = Number(minutes);
    if (!Number.isFinite(s) || !Number.isFinite(e) || !Number.isFinite(step)) return [];
    if (step <= 0) return [];
    if (e <= s) return [];
    const out = [];
    for (let t = s; t + step <= e; t += step) {
      out.push({ start_time: fromMin(t), end_time: fromMin(t + step) });
    }
    return out;
  };

  const bulkCreateSlots = async () => {
    if (!slotBulkForm.campus_id) {
      toast.error(t('settings.selectOptions.selectCampus'));
      return;
    }
    if (activeTab !== 'interview_slots') return;
    const days = slotBulkForm.weekdays || [];
    if (!days.length) {
      toast.error(t('settings.mailSendHours.selectWeekday'));
      return;
    }
    const mins = Number(slotBulkForm.slot_minutes);
    if (!Number.isFinite(mins) || mins <= 0 || mins > 720) {
      toast.error(t('settings.mailSendHours.slotMinutes'));
      return;
    }
    const slots = buildSlotsFromWindow({
      start: slotBulkForm.window_start,
      end: slotBulkForm.window_end,
      minutes: mins
    });
    if (!slots.length) {
      toast.error(t('settings.mailSendHours.invalidTimeWindow'));
      return;
    }

    setSlotBulkSaving(true);
    let created = 0;
    let skipped = 0;
    let failed = 0;
    try {
      for (const dow of days) {
        for (const s of slots) {
          try {
            await settingsAPI.createInterviewSlot({
              campus_id: slotBulkForm.campus_id,
              day_of_week: dow,
              start_time: s.start_time,
              end_time: s.end_time,
              interview_capacity: Number(slotBulkForm.interview_capacity) || 0,
              mock_capacity: Number(slotBulkForm.mock_capacity) || 0,
              status: 'active'
            });
            created += 1;
          } catch (e) {
            const msg = String(e?.response?.data?.message || e?.message || '');
            if (msg.toLowerCase().includes('already exists')) skipped += 1;
            else failed += 1;
          }
        }
      }
      await loadData();
      if (failed) toast.error(t('settings.interviewSlots.bulkCreatedWithFailed', { created, skipped, failed }));
      else toast.success(t('settings.interviewSlots.bulkCreated', { created, skipped }));
    } finally {
      setSlotBulkSaving(false);
    }
  };

  const timezoneSelectOptions = useMemo(() => {
    const t = mailWindowForm.timezone;
    if (t && !MAIL_TIMEZONES.includes(t)) return [t, ...MAIL_TIMEZONES];
    return [...MAIL_TIMEZONES];
  }, [MAIL_TIMEZONES, mailWindowForm.timezone]);

  const MAIL_ROLE_OPTIONS = useMemo(
    () => [
      { value: 'sales_executive', label: 'Sales Executive', hint: 'Leads & pipeline' },
      { value: 'interviewer', label: 'Interviewer', hint: 'Meetings & interviews' }
    ],
    []
  );

  const currentTab = tabs.find(t => t.key === activeTab);

  const loadData = useCallback(async () => {
    if (activeTab === 'mail_send_window' || activeTab === 'document_templates' || activeTab === 'prospect_round_robin' || activeTab === 'job_documents') {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let res;
      switch (activeTab) {
        case 'courses': res = await settingsAPI.getCourses({ include_inactive: 1 }); break;
        case 'program_types': res = await settingsAPI.getProgramTypes(); break;
        case 'intakes': res = await settingsAPI.getIntakes(); break;
        case 'sources': res = await settingsAPI.getSources(); break;
        case 'meeting_types': res = await settingsAPI.getMeetingTypes(); break;
        case 'prospect_statuses': res = await settingsAPI.getProspectStatuses(); break;
        case 'lead_statuses': res = await settingsAPI.getLeadStatuses(); break;
        case 'student_statuses': res = await settingsAPI.getStudentStatuses(); break;
        case 'job_student_statuses': res = await settingsAPI.getJobStudentStatuses(); break;
        case 'student_company_statuses': res = await settingsAPI.getStudentCompanyStatuses(); break;
        case 'visa_statuses': res = await settingsAPI.getVisaStatuses(); break;
        case 'visa_results': res = await settingsAPI.getVisaResults(); break;
        case 'campuses': res = await settingsAPI.getCampuses(); break;
        case 'course_campus_fees': res = await settingsAPI.getCourseCampusFees(); break;
        case 'document_types': res = await settingsAPI.getDocumentTypes({ category: 'documents' }); break;
        case 'visa_document_types': res = await settingsAPI.getDocumentTypes({ category: 'visa_documents' }); break;
        case 'interview_timezones': res = await settingsAPI.getInterviewTimezones(); break;
        case 'interview_slots': res = await settingsAPI.getInterviewSlots(); break;
        case 'mailboxes': res = await settingsAPI.getMailboxes(); break;
        case 'internship_programs': res = await settingsAPI.getInternshipPrograms(); break;
        case 'status_desired_options': res = await settingsAPI.getStatusDesiredOptions(); break;
        case 'business_specializations': res = await settingsAPI.getBusinessSpecializations(); break;
        case 'how_found_options': res = await settingsAPI.getHowFoundOptions(); break;
        default: res = { data: [] };
      }
      setData(res.data || []);
    } catch (err) { toast.error(t('settings.toasts.failedLoad')); }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (activeTab !== 'interview_slots') {
      setSelectedSlotIds([]);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'interview_slots') setSelectedSlotIds([]);
  }, [activeTab, slotWeekdayTab]);

  const slotRowsForDay = useMemo(() => {
    if (activeTab !== 'interview_slots') return data || [];
    const rows = data || [];
    return rows.filter((r) => Number(r.day_of_week) === Number(slotWeekdayTab));
  }, [activeTab, data, slotWeekdayTab]);

  const slotTableFields = useMemo(() => {
    if (activeTab !== 'interview_slots') return currentTab?.fields || [];
    return (currentTab?.fields || []).filter((f) => f !== 'day_of_week');
  }, [activeTab, currentTab?.fields]);

  const toggleSlotSelectAll = () => {
    const rows = activeTab === 'interview_slots' ? slotRowsForDay : (data || []);
    const ids = rows.map((r) => String(r.slot_id || r.id)).filter(Boolean);
    setSelectedSlotIds((prev) => {
      const cur = new Set((prev || []).map(String));
      const all = ids.length > 0 && ids.every((id) => cur.has(id));
      return all ? [] : ids;
    });
  };

  const settingsTableRows = activeTab === 'interview_slots' ? slotRowsForDay : data;
  const settingsTableFields = slotTableFields;

  useEffect(() => {
    if (activeTab !== 'zoom_hosts') return;
    setZoomHostsLoading(true);
    // Clear any stale form from previous country before the new row loads.
    setZoomHostsForm({
      interview_host_email: '',
      mock_interview_host_email: '',
      meeting_host_email: ''
    });
    settingsAPI
      .getZoomHosts()
      .then((res) => {
        const d = res.data || {};
        setZoomHostsForm({
          interview_host_email: d.interview_host_email || '',
          mock_interview_host_email: d.mock_interview_host_email || '',
          meeting_host_email: d.meeting_host_email || ''
        });
      })
      .catch(() => toast.error(t('settings.toasts.failedLoadZoom')))
      .finally(() => setZoomHostsLoading(false));
  }, [activeTab, activeCountry]);

  useEffect(() => {
    if (activeTab !== 'mail_send_window') return;
    setMailWindowLoading(true);
    settingsAPI
      .getMailSendWindow()
      .then((res) => {
        const d = res.data || {};
        setMailWindowForm({
          french_hours_enabled: !!d.french_hours_enabled,
          timezone: d.timezone || 'Europe/Paris',
          window_start: d.window_start || '08:00',
          window_end: d.window_end || '17:00',
          weekdays: Array.isArray(d.weekdays) && d.weekdays.length ? d.weekdays : [1, 2, 3, 4, 5],
          restricted_roles:
            Array.isArray(d.restricted_roles) && d.restricted_roles.length
              ? d.restricted_roles
              : ['sales_executive', 'interviewer']
        });
      })
      .catch(() => toast.error(t('settings.toasts.failedLoadMail')))
      .finally(() => setMailWindowLoading(false));
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'mailboxes') return;
    setSystemMailboxLoading(true);
    settingsAPI
      .getSystemMailbox()
      .then((res) => {
        const id = res?.data?.system_mailbox_id;
        setSystemMailboxId(id ? String(id) : '');
      })
      .catch(() => {
        setSystemMailboxId('');
        toast.error(t('common.failed'));
      })
      .finally(() => setSystemMailboxLoading(false));
  }, [activeTab, activeCountry, t]);

  const saveSystemMailbox = async () => {
    try {
      setSystemMailboxSaving(true);
      await settingsAPI.updateSystemMailbox({
        system_mailbox_id: systemMailboxId ? Number(systemMailboxId) : null
      });
      toast.success(t('settings.toasts.systemMailboxSaved'));
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    } finally {
      setSystemMailboxSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'prospect_round_robin') return;
    setRrLoading(true);
    settingsAPI
      .getWebsiteLeadRoundRobin()
      .then((res) => {
        const d = res.data || {};
        const selectedIds = Array.isArray(d.users)
          ? d.users.filter((u) => Number(u?.is_active)).map((u) => Number(u.user_id)).filter((x) => Number.isInteger(x))
          : [];
        setRoundRobinForm({
          enabled: !!Number(d.enabled),
          balance_by_status: d.balance_by_status === undefined ? true : !!Number(d.balance_by_status),
          preferred_status: d.preferred_status || '',
          user_ids: selectedIds
        });
      })
      .catch(() => toast.error(t('common.failed')))
      .finally(() => setRrLoading(false));
  }, [activeTab, t]);

  useEffect(() => {
    if (activeTab !== 'prospect_round_robin') return;
    settingsAPI
      .getProspectStatuses()
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        const activeRows = rows.filter((r) => String(r?.status || '').toLowerCase() === 'active');
        setWebsiteLeadStatuses(activeRows);
      })
      .catch(() => setWebsiteLeadStatuses([]));
  }, [activeTab]);

  const toggleMailWeekday = (n) => {
    setMailWindowForm((f) => {
      const set = new Set(f.weekdays || []);
      if (set.has(n)) set.delete(n);
      else set.add(n);
      return { ...f, weekdays: [...set].sort((a, b) => a - b) };
    });
  };

  const toggleMailRole = (value) => {
    setMailWindowForm((f) => {
      const set = new Set(f.restricted_roles || []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...f, restricted_roles: [...set].sort() };
    });
  };

  const saveMailSendWindow = async () => {
    if (!mailWindowForm.weekdays?.length) {
      toast.error(t('settings.mailSendHours.selectWeekday'));
      return;
    }
    if (mailWindowForm.french_hours_enabled && !mailWindowForm.restricted_roles?.length) {
      toast.error(t('settings.mailSendHours.selectRole'));
      return;
    }
    try {
      await settingsAPI.updateMailSendWindow({
        french_hours_enabled: mailWindowForm.french_hours_enabled,
        timezone: mailWindowForm.timezone,
        window_start: mailWindowForm.window_start,
        window_end: mailWindowForm.window_end,
        weekdays: mailWindowForm.weekdays,
        restricted_roles: mailWindowForm.restricted_roles
      });
      toast.success(t('settings.mailSendHours.saved'));
      const res = await settingsAPI.getMailSendWindow();
      const d = res.data || {};
      setMailWindowForm((prev) => ({
        ...prev,
        french_hours_enabled: !!d.french_hours_enabled,
        timezone: d.timezone || prev.timezone,
        window_start: d.window_start || prev.window_start,
        window_end: d.window_end || prev.window_end,
        weekdays: Array.isArray(d.weekdays) && d.weekdays.length ? d.weekdays : prev.weekdays,
        restricted_roles:
          Array.isArray(d.restricted_roles) && d.restricted_roles.length
            ? d.restricted_roles
            : prev.restricted_roles
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || t('common.failed'));
    }
  };

  const saveWebsiteLeadRoundRobin = async () => {
    try {
      setRrSaving(true);
      await settingsAPI.updateWebsiteLeadRoundRobin({
        enabled: roundRobinForm.enabled ? 1 : 0,
        balance_by_status: roundRobinForm.balance_by_status ? 1 : 0,
        preferred_status: roundRobinForm.preferred_status || null,
        user_ids: roundRobinForm.user_ids || []
      });
      toast.success(t('common.saved'));
      const res = await settingsAPI.getWebsiteLeadRoundRobin();
      const d = res.data || {};
      const selectedIds = Array.isArray(d.users)
        ? d.users.filter((u) => Number(u?.is_active)).map((u) => Number(u.user_id)).filter((x) => Number.isInteger(x))
        : [];
      setRoundRobinForm({
        enabled: !!Number(d.enabled),
        balance_by_status: d.balance_by_status === undefined ? true : !!Number(d.balance_by_status),
        preferred_status: d.preferred_status || '',
        user_ids: selectedIds
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    } finally {
      setRrSaving(false);
    }
  };

  useEffect(() => {
    Promise.all([settingsAPI.getCourses(), settingsAPI.getProgramTypes(), settingsAPI.getCampuses(), usersAPI.getAll()])
      .then(([coursesRes, programTypesRes, campusesRes, usersRes]) => {
        setCourses(coursesRes.data || []);
        setProgramTypes(programTypesRes.data || []);
        const activeCampuses = (campusesRes.data || []).filter(c => c.status === 'active');
        setCampuses(activeCampuses);
        setAllUsers(usersRes.data || []);
        setSlotBulkForm((prev) => ({
          ...prev,
          campus_id: prev.campus_id || activeCampuses[0]?.campus_id || ''
        }));
      })
      .catch(() => {});
  }, []);

  const handleAdd = () => {
    if (!currentTab?.fields?.length) return;
    setEditingItem(null);
    const newForm = {};
    currentTab.fields.forEach(f => {
      if (f === 'status') {
        newForm[f] = 'active';
      } else if (f === 'display_order') {
        newForm[f] = 0;
      } else if (f === 'auto_trigger') {
        newForm[f] = '';
      } else if (f === 'locks_pipeline') {
        newForm[f] = 0;
      } else if (f === 'day_of_week') {
        newForm[f] = 1;
      } else if (f === 'campus_id') {
        newForm[f] = campuses[0]?.campus_id || '';
      } else if (f === 'start_time') {
        newForm[f] = '10:00';
      } else if (f === 'end_time') {
        newForm[f] = '11:00';
      } else if (f === 'interview_capacity' || f === 'mock_capacity') {
        newForm[f] = 0;
      } else if (f === 'is_active' || f === 'is_default' || f === 'is_required' || f === 'mock_interview_enabled') {
        newForm[f] = 1;
      } else {
        newForm[f] = '';
      }
    });
    setForm(newForm);
    setPermittedUserIds([]);
    setMailboxUserSearch('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const newForm = {};
    currentTab.fields.forEach((f) => {
      if (f === 'locks_pipeline') {
        newForm[f] = Number(item[f]) ? 1 : 0;
      } else if (f === 'display_order') {
        newForm[f] = item[f] ?? 0;
      } else {
        newForm[f] = item[f] ?? '';
      }
    });
    setForm(newForm);
    setPermittedUserIds(
      (item.permitted_users || []).map((u) => u.user_id)
    );
    setMailboxUserSearch('');
    setShowModal(true);
  };

  const getIdFieldForTab = (tab) => {
    if (tab === 'program_types') return 'program_type_id';
    if (tab === 'prospect_statuses') return 'status_id';
    if (tab === 'lead_statuses') return 'status_id';
    if (tab === 'student_statuses') return 'status_id';
    if (tab === 'job_student_statuses') return 'status_id';
    if (tab === 'student_company_statuses') return 'status_id';
    if (tab === 'website_lead_statuses') return 'status_id';
    if (tab === 'visa_statuses') return 'status_id';
    if (tab === 'visa_results') return 'result_id';
    if (tab === 'document_types' || tab === 'visa_document_types') return 'document_type_id';
    if (tab === 'course_campus_fees') return 'id';
    if (tab === 'interview_timezones') return 'timezone_id';
    if (tab === 'campuses') return 'campus_id';
    if (tab === 'sources') return 'source_id';
    if (tab === 'courses') return 'course_id';
    if (tab === 'intakes') return 'intake_id';
    if (tab === 'meeting_types') return 'meeting_type_id';
    if (tab === 'interview_slots') return 'slot_id';
    if (tab === 'mailboxes') return 'mailbox_id';
    if (tab === 'internship_programs') return 'id';
    if (tab === 'status_desired_options') return 'id';
    if (tab === 'business_specializations') return 'id';
    if (tab === 'how_found_options') return 'id';
    return `${tab.slice(0, -1)}_id`;
  };

  const handleSubmit = async () => {
    if (activeTab === 'mail_send_window' || activeTab === 'document_templates' || activeTab === 'website_lead_round_robin') return;
    try {
      const tabKey = activeTab === 'visa_document_types' ? 'document_types' : activeTab;
      const idField = getIdFieldForTab(activeTab);
      const payload = { ...form };
      if (activeTab === 'document_types') payload.category = 'documents';
      if (activeTab === 'visa_document_types') payload.category = 'visa_documents';
      if (activeTab === 'interview_slots') {
        const slotPayload = {
          campus_id: payload.campus_id,
          day_of_week: payload.day_of_week,
          start_time: payload.start_time,
          end_time: payload.end_time,
          interview_capacity: payload.interview_capacity,
          mock_capacity: payload.mock_capacity,
          status: payload.status
        };
        if (editingItem) {
          await settingsAPI.updateInterviewSlot(editingItem[idField] || editingItem.id, slotPayload);
          toast.success(t('settings.toasts.updatedSuccess'));
        } else {
          await settingsAPI.createInterviewSlot(slotPayload);
          toast.success(t('settings.toasts.createdSuccess'));
        }
        setShowModal(false);
        loadData();
        return;
      }

      if (activeTab === 'mailboxes') {
        payload.permitted_user_ids = payload.send_restriction === 'specific_users' ? permittedUserIds : [];
      }
      if (editingItem) {
        if (activeTab === 'mailboxes') {
          await settingsAPI.updateMailbox(editingItem[idField] || editingItem.id, payload);
        } else {
          await settingsAPI.updateMasterData(tabKey, editingItem[idField] || editingItem.id, payload);
        }
        toast.success(t('settings.toasts.updatedSuccess'));
      } else {
        if (activeTab === 'mailboxes') {
          await settingsAPI.createMailbox(payload);
        } else {
          await settingsAPI.createMasterData(tabKey, payload);
        }
        toast.success(t('settings.toasts.createdSuccess'));
      }
      setShowModal(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.message || t('common.failed')); }
  };

  const handleDelete = async (item) => {
    if (activeTab === 'mail_send_window' || activeTab === 'document_templates' || activeTab === 'website_lead_round_robin') return;
    const confirmMsg = activeTab === 'mailboxes'
      ? t('settings.toasts.deleteMailboxConfirm')
      : (activeTab === 'interview_slots'
        ? t('settings.toasts.deleteSlotConfirm')
        : activeTab === 'student_statuses'
          ? t('settings.toasts.deleteStudentStatusConfirm')
          : t('settings.toasts.deactivateConfirm'));
    if (!window.confirm(confirmMsg)) return;
    try {
      const tabKey = activeTab === 'visa_document_types' ? 'document_types' : activeTab;
      const idField = getIdFieldForTab(activeTab);
      if (activeTab === 'interview_slots') {
        const id = item[idField] || item.id;
        await settingsAPI.deleteInterviewSlot(id);
        setSelectedSlotIds((prev) => (prev || []).filter((x) => String(x) !== String(id)));
        toast.success(t('settings.toasts.deleted'));
        await loadData();
        return;
      }
      if (activeTab === 'mailboxes') {
        await settingsAPI.deleteMailbox(item[idField] || item.id);
      } else {
        await settingsAPI.deleteMasterData(tabKey, item[idField] || item.id);
      }
      toast.success(
        activeTab === 'mailboxes'
          ? t('settings.toasts.mailboxDeleted')
          : activeTab === 'student_statuses'
            ? t('settings.toasts.deleted')
            : t('settings.toasts.deactivated')
      );
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || t('settings.toasts.deleteFailed'));
    }
  };

  const toggleSlotSelected = (slotId) => {
    const sid = String(slotId);
    setSelectedSlotIds((prev) => {
      const set = new Set((prev || []).map(String));
      if (set.has(sid)) set.delete(sid);
      else set.add(sid);
      return [...set];
    });
  };

  const bulkDeleteSelectedSlots = async () => {
    const ids = (selectedSlotIds || []).map(String).filter(Boolean);
    if (!ids.length) return;
    if (!window.confirm(t('settings.interviewSlots.bulkDeleteConfirm', { count: ids.length }))) return;
    try {
      await Promise.all(ids.map((id) => settingsAPI.deleteInterviewSlot(id)));
      setSelectedSlotIds([]);
      toast.success(t('settings.interviewSlots.bulkDeleted', { count: ids.length }));
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || t('settings.interviewSlots.bulkDeleteFailed'));
    }
  };

  const fieldLabels = {
    course_name: t('settings.fields.courseName'),
    course_name_fr: 'French Name',
    option_name: 'Option Name',
    option_name_fr: 'French Name',
    program_name: 'Program Name',
    specialization_name: 'Specialization Name',
    program_type: t('settings.fields.programType'),
    program_type_name: t('settings.fields.programType'),
    display_order: t('settings.fields.displayOrder'),
    duration: t('settings.fields.duration'),
    rncp_no: t('settings.fields.rncpNo'),
    rncp_title: t('settings.fields.rncpTitle'),
    rncp: t('settings.fields.rncpDetails'),
    intake_name: t('settings.fields.intakeName'),
    year: t('settings.fields.year'),
    source_name: t('settings.fields.sourceName'),
    type_name: t('settings.fields.typeName'),
    country: t('settings.fields.country'),
    city: t('settings.fields.city'),
    website: t('settings.fields.website'),
    address: t('settings.fields.address'),
    email: t('settings.fields.email'),
    phone: t('settings.fields.phone'),
    status: t('settings.fields.status'),
    prospect_group: t('settings.fields.prospectGroup'),
    lead_group: t('settings.fields.leadGroup'),
    status_name: t('settings.fields.statusName'),
    auto_trigger: t('settings.fields.autoTrigger'),
    locks_pipeline: t('settings.fields.locksPipeline'),
    result_name: t('settings.fields.resultName'),
    campus_name: t('settings.fields.campusName'),
    course_id: t('settings.fields.course'),
    campus_id: t('settings.fields.campus'),
    default_fee: t('settings.fields.defaultFee'),
    currency: t('settings.fields.currency'),
    document_type_name: t('settings.fields.documentType'),
    is_required: t('settings.fields.required'),
    is_passport: t('settings.fields.isPassport'),
    timezone_name: t('settings.fields.timezoneValue'),
    timezone_label: t('settings.fields.timezoneLabel'),
    mock_interview_enabled: t('settings.fields.mockInterview'),
    day_of_week: t('settings.fields.weekday'),
    start_time: t('settings.fields.startTime'),
    end_time: t('settings.fields.endTime'),
    interview_capacity: t('settings.fields.interviewSlots'),
    mock_capacity: t('settings.fields.mockInterviewSlots'),
    mailbox_name: t('settings.fields.mailboxName'),
    mailbox_email: t('settings.fields.mailboxEmail'),
    azure_tenant_id: t('settings.fields.azureTenantId'),
    azure_client_id: t('settings.fields.azureClientId'),
    azure_client_secret: t('settings.fields.azureClientSecret'),
    webhook_client_state: t('settings.fields.webhookClientState'),
    is_default: t('settings.fields.default'),
    is_active: t('settings.fields.active'),
    send_restriction: t('settings.fields.sendPermission'),
    name: t('settings.fields.templateName'),
    subject_template: t('settings.fields.subjectTemplate'),
    body_template: t('settings.fields.bodyTemplate')
  };

  const COMMON_IANA_TIMEZONES = useMemo(() => [
    'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
    'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Bogota', 'America/Chicago',
    'America/Denver', 'America/Los_Angeles', 'America/Mexico_City', 'America/New_York',
    'America/Sao_Paulo', 'America/Toronto', 'America/Vancouver',
    'Asia/Almaty', 'Asia/Baghdad', 'Asia/Bangkok', 'Asia/Colombo', 'Asia/Dhaka', 'Asia/Dubai',
    'Asia/Hong_Kong', 'Asia/Irkutsk', 'Asia/Istanbul', 'Asia/Jakarta', 'Asia/Karachi',
    'Asia/Kathmandu', 'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Muscat',
    'Asia/Novosibirsk', 'Asia/Riyadh', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore',
    'Asia/Taipei', 'Asia/Tashkent', 'Asia/Tehran', 'Asia/Tokyo', 'Asia/Vladivostok',
    'Asia/Yekaterinburg',
    'Atlantic/Reykjavik',
    'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
    'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Brussels', 'Europe/Bucharest',
    'Europe/Dublin', 'Europe/Helsinki', 'Europe/Istanbul', 'Europe/Kiev', 'Europe/Lisbon',
    'Europe/London', 'Europe/Madrid', 'Europe/Moscow', 'Europe/Paris', 'Europe/Prague',
    'Europe/Rome', 'Europe/Stockholm', 'Europe/Vienna', 'Europe/Warsaw', 'Europe/Zurich',
    'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu',
    'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific',
    'UTC'
  ], []);

  const getFieldType = (field) => {
    if (field === 'status') return 'select';
    if (field === 'timezone_name') return 'timezone_select';
    if (field === 'day_of_week') return 'weekday_select';
    if (field === 'start_time' || field === 'end_time') return 'time';
    if (field === 'program_type') return 'program_select';
    if (field === 'prospect_group') return 'prospect_group_select';
    if (field === 'lead_group') return 'lead_group_select';
    if (field === 'course_id') return 'course_select';
    if (field === 'campus_id') return 'campus_select';
    if (field === 'currency') return 'currency_select';
    if (field === 'mock_interview_enabled') return 'yes_no_select';
    if (field === 'locks_pipeline') return 'yes_no_select';
    if (field === 'auto_trigger') return 'student_auto_trigger_select';
    if (field === 'is_required') return 'required_select';
    if (field === 'is_passport') return 'yes_no_select';
    if (field === 'is_default' || field === 'is_active') return 'yes_no_select';
    if (field === 'send_restriction') return 'send_restriction_select';
    if (['year', 'interview_capacity', 'mock_capacity'].includes(field)) return 'number';
    if (['display_order'].includes(field)) return 'number';
    if (['default_fee'].includes(field)) return 'number_step';
    if (field === 'email') return 'email';
    if (['address', 'subject_template', 'body_template'].includes(field)) return 'textarea';
    return 'text';
  };

  const formatStatus = (val) => {
    if (!val) return '-';
    if (val === 'active' || val === 'Active') return t('common.active');
    if (val === 'inactive' || val === 'Inactive') return t('common.inactive');
    return val.charAt(0).toUpperCase() + val.slice(1);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiSettings style={{ marginRight: 8 }} />{t('settings.title')}</h2>
          <p className="page-subtitle">{t('settings.subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {tabs.map(tab => (
          <button key={tab.key} className={activeTab === tab.key ? 'active' : ''} onClick={() => setActiveTab(tab.key)}>
            {t(TAB_I18N[tab.key]) || tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab !== 'document_templates' && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <h3>
            {activeTab === 'mail_send_window'
              ? t('settings.tabs.mailSendHours')
              : activeTab === 'zoom_hosts'
                ? t('settings.zoomHosts.title')
                : activeTab === 'website_lead_round_robin'
                  ? t('settings.tabs.websiteLeadRoundRobin')
                : `${t(TAB_I18N[activeTab]) || currentTab?.label} (${data.length})`}
          </h3>
          {activeTab !== 'mail_send_window' && activeTab !== 'zoom_hosts' && activeTab !== 'website_lead_round_robin' && (
            <button className="btn btn-primary btn-sm" onClick={handleAdd}><FiPlus /> {t('settings.addItem', { item: (t(TAB_I18N[activeTab]) || currentTab?.label || '').replace(/s$/, '') || t('settings.itemFallback') })}</button>
          )}
        </div>
      )}

      {activeTab === 'document_templates' ? (
        <DocumentTemplateSettings />
      ) : activeTab === 'job_documents' ? (
        <JobDocumentsSettings />
      ) : activeTab === 'mail_send_window' ? (
        mailWindowLoading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : (
          <div style={MS.shell}>
            <div style={MS.card}>
              <div style={MS.hero}>
                <h4 style={MS.heroTitle}>{t('settings.mailSendHours.title')}</h4>
                <p style={MS.heroSub}>
                  {t('settings.mailSendHours.desc')}
                </p>
              </div>

              <div style={MS.section}>
                <label
                  style={{ ...MS.toggleRow, borderColor: mailWindowForm.french_hours_enabled ? '#a5b4fc' : 'var(--border)' }}
                >
                  <input
                    type="checkbox"
                    checked={mailWindowForm.french_hours_enabled}
                    onChange={(e) => setMailWindowForm((f) => ({ ...f, french_hours_enabled: e.target.checked }))}
                    style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{t('settings.mailSendHours.restrictLabel')}</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      {t('settings.mailSendHours.desc')}
                    </div>
                  </div>
                </label>
                <div style={{ ...MS.callout, marginTop: 16 }}>
                  <FiShield size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>
                    <strong>{t('settings.mailSendHours.alwaysExempt')}</strong> {t('settings.mailSendHours.exemptDesc')}
                  </span>
                </div>
              </div>

              <div style={MS.section}>
                <div style={MS.rowHead}>
                  <div style={MS.iconBox}><FiUsers size={20} /></div>
                  <div>
                    <h5 style={MS.secTitle}>{t('settings.mailSendHours.appliesToRoles')}</h5>
                    <p style={MS.secHint}>{t('settings.mailSendHours.rolesHint')}</p>
                  </div>
                </div>
                <div style={{ ...MS.grid2, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                  {MAIL_ROLE_OPTIONS.map((role) => {
                    const on = mailWindowForm.restricted_roles?.includes(role.value);
                    return (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => toggleMailRole(role.value)}
                        style={MS.chip(on)}
                      >
                        <span style={MS.chipTitle}>{role.value === 'sales_executive' ? t('settings.mailRoles.salesExecutive') : t('settings.mailRoles.interviewer')}</span>
                        <span style={MS.chipHint}>{role.value === 'sales_executive' ? t('settings.mailRoles.salesHint') : t('settings.mailRoles.interviewerHint')}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={MS.section}>
                <div style={MS.rowHead}>
                  <div style={MS.iconBox}><FiClock size={20} /></div>
                  <div>
                    <h5 style={MS.secTitle}>{t('settings.mailSendHours.timeWindow')}</h5>
                    <p style={MS.secHint}>{t('settings.mailSendHours.timeWindowHint')}</p>
                  </div>
                </div>
                <div style={MS.grid2}>
                  <div>
                    <span style={MS.label}>{t('settings.mailSendHours.timezoneLabel')}</span>
                    <select
                      style={MS.input}
                      value={mailWindowForm.timezone}
                      onChange={(e) => setMailWindowForm((f) => ({ ...f, timezone: e.target.value }))}
                    >
                      {timezoneSelectOptions.map((tz) => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span style={MS.label}>{t('settings.mailSendHours.startEnd')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <input
                        type="time"
                        value={mailWindowForm.window_start}
                        onChange={(e) => setMailWindowForm((f) => ({ ...f, window_start: e.target.value }))}
                        style={MS.timeInput}
                      />
                      <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>→</span>
                      <input
                        type="time"
                        value={mailWindowForm.window_end}
                        onChange={(e) => setMailWindowForm((f) => ({ ...f, window_end: e.target.value }))}
                        style={MS.timeInput}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={MS.sectionLast}>
                <div style={MS.rowHead}>
                  <div style={MS.iconBox}><FiCalendar size={20} /></div>
                  <div>
                    <h5 style={MS.secTitle}>{t('settings.mailSendHours.allowedDays')}</h5>
                    <p style={MS.secHint}>{t('settings.mailSendHours.allowedDaysHint')}</p>
                  </div>
                </div>
                <div style={MS.dayRow}>
                  {MAIL_WEEKDAYS.map(({ n, label }) => {
                    const on = mailWindowForm.weekdays?.includes(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => toggleMailWeekday(n)}
                        style={MS.dayChip(on)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ ...MS.callout, marginTop: 18, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>
                  <FiInfo size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>
                    {t('settings.mailSendHours.infoCallout')}
                  </span>
                </div>
              </div>

              <div style={MS.footer}>
                <button type="button" className="btn btn-primary" onClick={saveMailSendWindow} style={{ minWidth: 140 }}>
                  <FiSave /> {t('settings.mailSendHours.saveBtn')}
                </button>
              </div>
            </div>
          </div>
        )
      ) : activeTab === 'zoom_hosts' ? (
        zoomHostsLoading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : (
          <div style={{ maxWidth: 720 }}>
            <div className="card">
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {t('settings.zoomHosts.desc')}
                </div>

                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>{t('settings.zoomHosts.interviewLabel')}</label>
                    <input
                      type="email"
                      value={zoomHostsForm.interview_host_email}
                      onChange={(e) => setZoomHostsForm((f) => ({ ...f, interview_host_email: e.target.value }))}
                      placeholder={t('settings.zoomHosts.interviewPlaceholder')}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>{t('settings.zoomHosts.mockLabel')}</label>
                    <input
                      type="email"
                      value={zoomHostsForm.mock_interview_host_email}
                      onChange={(e) => setZoomHostsForm((f) => ({ ...f, mock_interview_host_email: e.target.value }))}
                      placeholder={t('settings.zoomHosts.mockPlaceholder')}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>{t('settings.zoomHosts.meetingLabel')}</label>
                    <input
                      type="email"
                      value={zoomHostsForm.meeting_host_email}
                      onChange={(e) => setZoomHostsForm((f) => ({ ...f, meeting_host_email: e.target.value }))}
                      placeholder={t('settings.zoomHosts.meetingPlaceholder')}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-primary"
                    disabled={zoomHostsSaving}
                    onClick={async () => {
                      try {
                        setZoomHostsSaving(true);
                        await settingsAPI.updateZoomHosts(zoomHostsForm);
                        toast.success(t('settings.zoomHosts.saved'));
                      } catch (e) {
                        toast.error(e?.response?.data?.message || t('settings.zoomHosts.saveFailed'));
                      } finally {
                        setZoomHostsSaving(false);
                      }
                    }}
                  >
                    <FiSave style={{ marginRight: 6 }} /> {zoomHostsSaving ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      ) : activeTab === 'prospect_round_robin' ? (
        rrLoading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : (
          <div style={{ maxWidth: 920 }}>
            <div className="card">
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Auto-assign incoming website leads (prospects) in balanced way across selected users.
                </div>

                <label style={{ ...MS.toggleRow, borderColor: roundRobinForm.enabled ? '#a5b4fc' : 'var(--border)' }}>
                  <input
                    type="checkbox"
                    checked={!!roundRobinForm.enabled}
                    onChange={(e) => setRoundRobinForm((f) => ({ ...f, enabled: e.target.checked }))}
                    style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Enable round robin assignment</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      New prospects from website leads will be assigned automatically.
                    </div>
                  </div>
                </label>

                <label style={{ ...MS.toggleRow, borderColor: roundRobinForm.balance_by_status ? '#a5b4fc' : 'var(--border)' }}>
                  <input
                    type="checkbox"
                    checked={!!roundRobinForm.balance_by_status}
                    onChange={(e) => setRoundRobinForm((f) => ({ ...f, balance_by_status: e.target.checked }))}
                    style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Balance by status first</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      If enabled, assignment prefers users with lower count for the current status.
                    </div>
                  </div>
                </label>

                {roundRobinForm.balance_by_status && (
                  <div className="form-group">
                    <label style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>Balance using status</label>
                    <select
                      className="form-input"
                      value={roundRobinForm.preferred_status || ''}
                      onChange={(e) => setRoundRobinForm((f) => ({ ...f, preferred_status: e.target.value }))}
                    >
                      <option value="">{t('settings.selectOptions.autoUseProspectStatus', 'Auto (use prospect status)')}</option>
                      {websiteLeadStatuses.map((s) => (
                        <option key={s.status_id || s.status_name} value={s.status_name}>
                          {s.status_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <strong>Select users for assignment</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Selected: {roundRobinForm.user_ids.length}
                    </span>
                  </div>
                  <div style={{ marginTop: 10, position: 'relative' }}>
                    <FiSearch
                      size={15}
                      style={{
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)',
                        pointerEvents: 'none'
                      }}
                    />
                    <input
                      type="text"
                      value={rrUserSearch}
                      onChange={(e) => setRrUserSearch(e.target.value)}
                      placeholder="Search France users by name or email"
                      style={{
                        width: '100%',
                        height: 38,
                        borderRadius: 10,
                        border: '1px solid var(--border)',
                        background: 'var(--white)',
                        color: 'var(--text)',
                        padding: '0 12px 0 36px',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 10, maxHeight: 320, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
                    {(rrUsers || []).map((u) => {
                      const uid = Number(u.user_id);
                      const checked = roundRobinForm.user_ids.includes(uid);
                      return (
                        <label
                          key={uid}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                            padding: '10px 12px',
                            borderBottom: '1px solid var(--border-light, #eee)',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setRoundRobinForm((f) => {
                                  const set = new Set(f.user_ids || []);
                                  if (e.target.checked) set.add(uid); else set.delete(uid);
                                  return { ...f, user_ids: [...set] };
                                });
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: 600 }}>{u.name}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}</div>
                            </div>
                          </div>
                          <span className="badge" style={{ textTransform: 'capitalize' }}>{u.role || '-'}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div style={{ ...MS.callout, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>
                  <FiInfo size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>
                    Round robin works for France prospects from website forms and applies during new public form submissions.
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-primary"
                    disabled={rrSaving}
                    onClick={saveWebsiteLeadRoundRobin}
                  >
                    <FiSave style={{ marginRight: 6 }} /> {rrSaving ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      ) : loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div className="table-container">
          {activeTab === 'mailboxes' && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ fontWeight: 700 }}>
                  System Mailbox
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Default mailbox for system-generated CRM emails.
                </div>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Select mailbox</label>
                    <select
                      value={systemMailboxId}
                      onChange={(e) => setSystemMailboxId(e.target.value)}
                      disabled={systemMailboxLoading || systemMailboxSaving}
                    >
                      <option value="">{t('common.none', 'None')}</option>
                      {(data || [])
                        .filter((m) => Number(m?.is_active) === 1)
                        .map((m) => (
                          <option key={m.mailbox_id} value={m.mailbox_id}>
                            {m.mailbox_name} - {m.mailbox_email}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveSystemMailbox}
                    disabled={systemMailboxLoading || systemMailboxSaving}
                  >
                    <FiSave style={{ marginRight: 6 }} />
                    {systemMailboxSaving ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'interview_slots' && (
            <div style={{
              border: '1px solid var(--border)',
              background: 'var(--white)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 14
            }}>
              {(() => {
                const days = slotBulkForm.weekdays || [];
                const slots = buildSlotsFromWindow({
                  start: slotBulkForm.window_start,
                  end: slotBulkForm.window_end,
                  minutes: Number(slotBulkForm.slot_minutes)
                });
                const total = days.length * (slots.length || 0);
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 900, fontSize: 15 }}>{t('settings.interviewSlots.title')}</div>
                        <div style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.45 }}>
                          {t('settings.interviewSlots.desc')}
                        </div>
                        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span className="badge badge-new" style={{ fontSize: 12 }}>
                            {days.length} {t('settings.interviewSlots.daysBadge')}
                          </span>
                          <span className="badge badge-followup" style={{ fontSize: 12 }}>
                            {slots.length || 0} {t('settings.interviewSlots.slotsBadge')}
                          </span>
                          <span className="badge" style={{ fontSize: 12, background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                            {t('settings.interviewSlots.totalLabel')} {Number.isFinite(total) ? total : 0}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {selectedSlotIds.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={bulkDeleteSelectedSlots}
                            title={t('settings.interviewSlots.deleteSelectedTitle')}
                          >
                            <FiTrash2 /> {t('settings.interviewSlots.deleteSelected', { count: selectedSlotIds.length })}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={bulkCreateSlots}
                          disabled={slotBulkSaving}
                          title={t('settings.interviewSlots.generateTitle')}
                        >
                          <FiPlus /> {slotBulkSaving ? t('common.creating') : t('settings.interviewSlots.generateBtn')}
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--bg-secondary)' }}>
                        <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 8 }}>{t('settings.fields.campus')}</div>
                        <select
                          value={slotBulkForm.campus_id}
                          onChange={(e) => setSlotBulkForm((f) => ({ ...f, campus_id: e.target.value }))}
                        >
                          <option value="">{t('settings.selectOptions.selectCampus')}</option>
                          {campuses.map(c => <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>)}
                        </select>
                      </div>

                      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--bg-secondary)' }}>
                        <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 8 }}>{t('settings.interviewSlots.selectWeekdays')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {SLOT_WEEKDAYS.map(({ n, label }) => {
                            const on = slotBulkForm.weekdays?.includes(n);
                            return (
                              <button
                                key={n}
                                type="button"
                                onClick={() => toggleBulkWeekday(n)}
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  border: `1px solid ${on ? '#4f46e5' : 'var(--border)'}`,
                                  background: on ? '#eef2ff' : 'var(--white)',
                                  fontWeight: 800,
                                  fontSize: 13,
                                  cursor: 'pointer',
                                  color: on ? '#3730a3' : 'var(--text-muted)'
                                }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--bg-secondary)' }}>
                        <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 8 }}>{t('settings.interviewSlots.timeRange')}</div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="time"
                            value={slotBulkForm.window_start}
                            onChange={(e) => setSlotBulkForm((f) => ({ ...f, window_start: e.target.value }))}
                          />
                          <span style={{ color: 'var(--text-muted)', fontWeight: 900 }}>{t('settings.interviewSlots.to')}</span>
                          <input
                            type="time"
                            value={slotBulkForm.window_end}
                            onChange={(e) => setSlotBulkForm((f) => ({ ...f, window_end: e.target.value }))}
                          />
                        </div>
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 6 }}>{t('settings.interviewSlots.slotLength')}</div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {[30, 45, 60].map((m) => (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setSlotBulkForm((f) => ({ ...f, slot_minutes: m }))}
                                className="btn btn-sm btn-outline"
                                style={{ padding: '6px 10px' }}
                              >
                                {m} min
                              </button>
                            ))}
                            <input
                              type="number"
                              min="15"
                              step="15"
                              value={slotBulkForm.slot_minutes}
                              onChange={(e) => setSlotBulkForm((f) => ({ ...f, slot_minutes: Number(e.target.value) }))}
                              style={{ width: 110 }}
                            />
                          </div>
                        </div>
                      </div>

                      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--bg-secondary)' }}>
                        <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 8 }}>{t('settings.interviewSlots.capacityPerSlot')}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 800 }}>{t('settings.interviewSlots.interviewCap')}</span>
                            <input
                              type="number"
                              min="0"
                              value={slotBulkForm.interview_capacity}
                              onChange={(e) => setSlotBulkForm((f) => ({ ...f, interview_capacity: Number(e.target.value) }))}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 800 }}>{t('settings.interviewSlots.mockCap')}</span>
                            <input
                              type="number"
                              min="0"
                              value={slotBulkForm.mock_capacity}
                              onChange={(e) => setSlotBulkForm((f) => ({ ...f, mock_capacity: Number(e.target.value) }))}
                            />
                          </div>
                        </div>
                        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                          {t('settings.interviewSlots.capacityHint')}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          {activeTab === 'interview_slots' ? (
            <div
              style={{
                border: '1px solid var(--border)',
                background: 'var(--white)',
                borderRadius: 12,
                padding: 16
              }}
            >
              <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 900, fontSize: 14, marginBottom: 10 }}>{t('settings.interviewSlots.title')}</div>
                <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                  {t('settings.interviewSlots.viewByWeekday')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {MAIL_WEEKDAYS.map(({ n, label }) => {
                    const count = (data || []).filter((r) => Number(r.day_of_week) === n).length;
                    const active = slotWeekdayTab === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setSlotWeekdayTab(n)}
                        style={{
                          padding: '10px 14px',
                          borderRadius: 10,
                          border: `1px solid ${active ? '#4f46e5' : 'var(--border)'}`,
                          background: active ? '#eef2ff' : 'var(--white)',
                          fontWeight: 800,
                          fontSize: 13,
                          cursor: 'pointer',
                          color: active ? '#3730a3' : 'var(--text-muted)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6
                        }}
                      >
                        <span>{label}</span>
                        {count > 0 && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '2px 7px',
                              borderRadius: 999,
                              background: active ? 'rgba(79, 70, 229, 0.22)' : 'var(--bg-secondary)',
                              color: active ? '#3730a3' : 'var(--text-muted)'
                            }}
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {t(`settings.weekdaysFull.${['monday','tuesday','wednesday','thursday','friday','saturday','sunday'][slotWeekdayTab - 1]}`)}
                  </strong>
                  {' — '}
                  {t('settings.interviewSlots.slotsForDay', { count: slotRowsForDay.length })}
                </div>
              </div>

              <div className="table-responsive" style={{ marginTop: 6 }}>
              <table>
            <thead>
              <tr>
                {activeTab === 'interview_slots' && (
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={(() => {
                        const ids = slotRowsForDay.map((r) => String(r.slot_id || r.id)).filter(Boolean);
                        return ids.length > 0 && ids.every((id) => selectedSlotIds.map(String).includes(id));
                      })()}
                      onChange={toggleSlotSelectAll}
                      title={t('settings.interviewSlots.selectAllDay')}
                    />
                  </th>
                )}
                {settingsTableFields.map((f) => (
                  <th key={f}>{fieldLabels[f] || f}</th>
                ))}
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {settingsTableRows.map((item, idx) => (
                <tr key={activeTab === 'interview_slots' ? String(item.slot_id || item.id || idx) : idx}>
                  {activeTab === 'interview_slots' && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSlotIds.map(String).includes(String(item.slot_id || item.id))}
                        onChange={() => toggleSlotSelected(item.slot_id || item.id)}
                      />
                    </td>
                  )}
                  {settingsTableFields.map((f) => (
                    <td key={f}>
                      {f === 'status' ? (
                        <span style={{
                          padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          color: (item[f] === 'active' || item[f] === 'Active') ? 'var(--success)' : 'var(--danger)',
                          background: (item[f] === 'active' || item[f] === 'Active') ? 'var(--success-light)' : 'var(--danger-light)'
                        }}>
                          {formatStatus(item[f])}
                        </span>
                      ) : f === 'day_of_week' ? (
                        ({ 1: t('settings.weekdays.mon'), 2: t('settings.weekdays.tue'), 3: t('settings.weekdays.wed'), 4: t('settings.weekdays.thu'), 5: t('settings.weekdays.fri'), 6: t('settings.weekdays.sat'), 7: t('settings.weekdays.sun') }[Number(item[f])] || item[f] || '-')
                      ) : f.includes('date') ? (
                        item[f] ? new Date(item[f]).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'
                      ) : f === 'course_id' ? (
                        item.course_name || '-'
                      ) : f === 'campus_id' ? (
                        item.campus_name || '-'
                      ) : f === 'mock_interview_enabled' || f === 'locks_pipeline' || f === 'is_passport' ? (
                        Number(item[f]) ? t('common.yes') : t('common.no')
                      ) : f === 'auto_trigger' ? (
                        item[f]
                          ? t(`settings.studentAutoTriggers.${item[f]}`, { defaultValue: item[f] })
                          : t('settings.studentAutoTriggers.manual')
                      ) : (f === 'is_default' || f === 'is_active') ? (
                        Number(item[f]) ? t('common.yes') : t('common.no')
                      ) : f === 'send_restriction' ? (
                        item[f] === 'admin_only'
                          ? t('settings.selectOptions.adminOnly')
                          : item[f] === 'specific_users'
                            ? `${t('settings.selectOptions.specificUsers')} (${(item.permitted_users || []).length})`
                            : t('settings.selectOptions.allUsers')
                      ) : (
                        item[f] || '-'
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="table-actions">
                      <button
                        className="edit"
                        onClick={() => handleEdit(item)}
                        title={t('common.edit')}
                      >
                        <FiEdit />
                      </button>
                      <button
                        className="delete"
                        onClick={() => handleDelete(item)}
                        title={t('common.delete')}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {settingsTableRows.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      settingsTableFields.length +
                      1 +
                      (activeTab === 'interview_slots' ? 1 : 0)
                    }
                    style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                  >
                    {activeTab === 'interview_slots' && (data || []).length > 0
                      ? t('settings.interviewSlots.noSlotsForDay', { weekday: t(`settings.weekdaysFull.${['monday','tuesday','wednesday','thursday','friday','saturday','sunday'][slotWeekdayTab - 1]}`) })
                      : t('settings.noItemsFound', { label: (t(TAB_I18N[activeTab]) || currentTab?.label || '').toLowerCase() })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  {settingsTableFields.map((f) => (
                    <th key={f}>{fieldLabels[f] || f}</th>
                  ))}
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {settingsTableRows.map((item, idx) => (
                  <tr key={idx}>
                    {settingsTableFields.map((f) => (
                      <td key={f}>
                        {f === 'status' ? (
                          <span style={{
                            padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            color: (item[f] === 'active' || item[f] === 'Active') ? 'var(--success)' : 'var(--danger)',
                            background: (item[f] === 'active' || item[f] === 'Active') ? 'var(--success-light)' : 'var(--danger-light)'
                          }}>
                            {formatStatus(item[f])}
                          </span>
                        ) : f === 'day_of_week' ? (
                          ({ 1: t('settings.weekdays.mon'), 2: t('settings.weekdays.tue'), 3: t('settings.weekdays.wed'), 4: t('settings.weekdays.thu'), 5: t('settings.weekdays.fri'), 6: t('settings.weekdays.sat'), 7: t('settings.weekdays.sun') }[Number(item[f])] || item[f] || '-')
                        ) : f.includes('date') ? (
                          item[f] ? new Date(item[f]).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'
                        ) : f === 'course_id' ? (
                          item.course_name || '-'
                        ) : f === 'campus_id' ? (
                          item.campus_name || '-'
                        ) : f === 'mock_interview_enabled' || f === 'is_passport' ? (
                          Number(item[f]) ? t('common.yes') : t('common.no')
                        ) : (f === 'is_default' || f === 'is_active') ? (
                          Number(item[f]) ? t('common.yes') : t('common.no')
                        ) : f === 'send_restriction' ? (
                          item[f] === 'admin_only'
                            ? t('settings.selectOptions.adminOnly')
                            : item[f] === 'specific_users'
                              ? `${t('settings.selectOptions.specificUsers')} (${(item.permitted_users || []).length})`
                              : t('settings.selectOptions.allUsers')
                        ) : (
                          item[f] || '-'
                        )}
                      </td>
                    ))}
                    <td>
                      <div className="table-actions">
                        <button
                          className="edit"
                          onClick={() => handleEdit(item)}
                          title={t('common.edit')}
                        >
                          <FiEdit />
                        </button>
                        <button
                          className="delete"
                          onClick={() => handleDelete(item)}
                          title={t('common.delete')}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {settingsTableRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        settingsTableFields.length +
                        1
                      }
                      style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                    >
                      {t('settings.noItemsFound', { label: (t(TAB_I18N[activeTab]) || currentTab?.label || '').toLowerCase() })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editingItem ? t('settings.editItem', { item: (t(TAB_I18N[activeTab]) || currentTab?.label || '').replace(/s$/, '') }) : t('settings.addItemModal', { item: (t(TAB_I18N[activeTab]) || currentTab?.label || '').replace(/s$/, '') })}
        footer={<><button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={handleSubmit}><FiSave /> {editingItem ? t('common.update') : t('common.save')}</button></>}>
        <div className="form-grid">
          {currentTab?.fields.map(field => {
            const type = getFieldType(field);
            return (
              <div className={`form-group ${['address'].includes(field) ? 'full-width' : ''}`} key={field}>
                <label>{fieldLabels[field] || field}</label>
                {type === 'select' ? (
                  <select value={form[field] || ''} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}>
                    <option value="active">{t('common.active')}</option>
                    <option value="inactive">{t('common.inactive')}</option>
                  </select>
                ) : type === 'timezone_select' ? (
                  <select
                    value={form[field] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm(f => ({
                        ...f,
                        [field]: val,
                        timezone_label: val ? `${val} (${(() => { try { const off = new Intl.DateTimeFormat('en-GB', { timeZone: val, timeZoneName: 'shortOffset' }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value; return off || ''; } catch { return ''; }})()})` : f.timezone_label
                      }));
                    }}
                  >
                    <option value="">{t('settings.selectOptions.selectTimezone')}</option>
                    {COMMON_IANA_TIMEZONES.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                ) : type === 'weekday_select' ? (
                  <select
                    value={String(form[field] ?? '')}
                    onChange={(e) => setForm(f => ({ ...f, [field]: Number(e.target.value) }))}
                  >
                    <option value="">{t('settings.selectOptions.selectWeekday')}</option>
                    <option value="1">{t('settings.weekdays.mon')}</option>
                    <option value="2">{t('settings.weekdays.tue')}</option>
                    <option value="3">{t('settings.weekdays.wed')}</option>
                    <option value="4">{t('settings.weekdays.thu')}</option>
                    <option value="5">{t('settings.weekdays.fri')}</option>
                    <option value="6">{t('settings.weekdays.sat')}</option>
                    <option value="7">{t('settings.weekdays.sun')}</option>
                  </select>
                ) : type === 'prospect_group_select' ? (
                  <select value={form[field] || ''} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}>
                    <option value="">{t('settings.selectOptions.selectProspectGroup')}</option>
                    <option value="B2B">{t('common.b2b', 'B2B')}</option>
                    <option value="B2C">{t('common.b2c', 'B2C')}</option>
                  </select>
                ) : type === 'lead_group_select' ? (
                  <select value={form[field] || ''} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}>
                    <option value="">{t('settings.selectOptions.selectLeadGroup')}</option>
                    <option value="B2B">{t('common.b2b', 'B2B')}</option>
                    <option value="B2C">{t('common.b2c', 'B2C')}</option>
                  </select>
                ) : type === 'program_select' ? (
                  <select value={form[field] || ''} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}>
                    <option value="">{t('settings.selectOptions.selectProgram')}</option>
                    {programTypes.map((p) => (
                      <option key={p.program_type_id || p.program_type_name} value={p.program_type_name}>
                        {p.program_type_name}
                      </option>
                    ))}
                  </select>
                ) : type === 'course_select' ? (
                  <select value={form[field] || ''} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}>
                    <option value="">{t('settings.selectOptions.selectCourse')}</option>
                    {courses.map(c => <option key={c.course_id} value={c.course_id}>{c.course_name}</option>)}
                  </select>
                ) : type === 'campus_select' ? (
                  <select value={form[field] || ''} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}>
                    <option value="">{t('settings.selectOptions.selectCampus')}</option>
                    {campuses.map(c => <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>)}
                  </select>
                ) : type === 'currency_select' ? (
                  <select value={form[field] || 'AED'} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}>
                    {['AED', 'EURO'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : type === 'required_select' ? (
                  <select value={String(form[field] ?? '0')} onChange={(e) => setForm(f => ({ ...f, [field]: Number(e.target.value) }))}>
                    <option value="1">{t('common.yes')}</option>
                    <option value="0">{t('common.no')}</option>
                  </select>
                ) : type === 'yes_no_select' ? (
                  <select value={String(form[field] ?? '0')} onChange={(e) => setForm(f => ({ ...f, [field]: Number(e.target.value) }))}>
                    <option value="1">{t('common.yes')}</option>
                    <option value="0">{t('common.no')}</option>
                  </select>
                ) : type === 'student_auto_trigger_select' ? (
                  <select
                    value={form[field] ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                  >
                    {STUDENT_AUTO_TRIGGER_OPTIONS.map((o) => (
                      <option key={o.value || 'none'} value={o.value}>
                        {t(o.labelKey)}
                      </option>
                    ))}
                  </select>
                ) : type === 'send_restriction_select' ? (
                  <>
                    <select value={form[field] || 'all'} onChange={(e) => {
                      setForm(f => ({ ...f, [field]: e.target.value }));
                      if (e.target.value !== 'specific_users') {
                        setPermittedUserIds([]);
                        setMailboxUserSearch('');
                      }
                    }}>
                      <option value="all">{t('settings.selectOptions.allUsers')}</option>
                      <option value="admin_only">{t('settings.selectOptions.adminOnly')}</option>
                      <option value="specific_users">{t('settings.selectOptions.specificUsers')}</option>
                    </select>
                    {form[field] === 'specific_users' && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                            <input
                              type="text"
                              value={mailboxUserSearch}
                              onChange={(e) => setMailboxUserSearch(e.target.value)}
                              placeholder={t('settings.selectOptions.searchUsers')}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                borderRadius: 10,
                                border: '1px solid var(--border)',
                                background: 'var(--white)',
                                fontSize: 14
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => setPermittedUserIds(filteredMailboxUsers.map((u) => u.user_id))}
                            disabled={!filteredMailboxUsers.length}
                          >
                            {t('settings.selectOptions.selectAllUsers')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => setPermittedUserIds([])}
                            disabled={!permittedUserIds.length}
                          >
                            {t('settings.selectOptions.clearUsers')}
                          </button>
                        </div>

                        {selectedMailboxUsers.length > 0 && (
                          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {selectedMailboxUsers.map((u) => (
                              <button
                                key={u.user_id}
                                type="button"
                                  className="btn btn-outline btn-sm"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  border: '1px solid var(--border)',
                                  borderRadius: 999,
                                  padding: '6px 10px',
                                  background: 'var(--white)'
                                }}
                                onClick={() => setPermittedUserIds((prev) => prev.filter((id) => String(id) !== String(u.user_id)))}
                                title={t('settings.selectOptions.removeUser')}
                              >
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{u.name || u.email}</span>
                                <span style={{ color: 'var(--text-muted)', fontWeight: 800, lineHeight: 1 }}>×</span>
                              </button>
                            ))}
                          </div>
                        )}

                        <div
                          style={{
                            marginTop: 10,
                            border: '1px solid var(--border)',
                            borderRadius: 10,
                            padding: 10,
                            maxHeight: 240,
                            overflowY: 'auto',
                            background: 'var(--white)'
                          }}
                        >
                          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                            {t('settings.selectOptions.selectUsersHint')}
                            {filteredMailboxUsers.length ? ` (${filteredMailboxUsers.length} shown)` : ''}
                          </p>

                          {filteredMailboxUsers.length === 0 ? (
                            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>{t('users.noUsers')}</p>
                          ) : (
                            filteredMailboxUsers.map((u) => {
                              const checked = permittedUserIds.includes(u.user_id);
                              return (
                                <label
                                  key={u.user_id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '8px 8px',
                                    borderRadius: 10,
                                    cursor: 'pointer',
                                    border: checked ? '1px solid #e0e7ff' : '1px solid transparent',
                                    background: checked ? '#f5f3ff' : 'transparent'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    style={{
                                      width: 18,
                                      height: 18,
                                      margin: 0,
                                      flexShrink: 0,
                                      accentColor: '#4f46e5'
                                    }}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setPermittedUserIds((prev) => [...prev, u.user_id]);
                                      } else {
                                        setPermittedUserIds((prev) => prev.filter((id) => String(id) !== String(u.user_id)));
                                      }
                                    }}
                                  />
                                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{u.name || '-'}</span>
                                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                      {u.email || '-'} · {u.role || '-'}
                                    </span>
                                  </div>
                                </label>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : type === 'textarea' ? (
                  <textarea value={form[field] || ''} onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))} />
                ) : (
                  <input
                    type={type === 'number_step' ? 'number' : type}
                    step={type === 'number_step' ? '0.01' : undefined}
                    value={form[field] || ''}
                    onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default Settings;
